<script>
import WebLayout from '@/layouts/Web/Layout.vue'

export default {
    // Using shorthand syntax...
    layout: WebLayout,
}
</script>
<script setup>
import SEOHead from '@/components/SEOHead.vue'
</script>

<template>
    <SEOHead
        title="Pogoji uporabe - Kulfood"
        description="Splošni pogoji uporabe in poslovanja za Kulfood. Preberite si pravila naročanja, odpovedi, reklamacij in varstva podatkov."
        keywords="pogoji uporabe, Kulfood.si"
        canonical="/pogoji-uporabe"
        :structured-data="{
            '@type': 'WebSite',
            'name': 'Kulfood.si',
            'url': 'https://kulfood.si',
            'description': 'Splošni pogoji uporabe in poslovanja za Kulfood. Preberite si pravila naročanja, odpovedi, reklamacij in varstva podatkov.',
        }"
        type="website"
    />
    <div class="container mx-auto px-4 py-12">
        <div class="max-w-4xl mx-auto">
            <h1 class="text-4xl font-bold text-gray-900 mb-8 text-center">SPLOŠNI POGOJI UPORABE IN POSLOVANJA</h1>
            
            <div class="prose prose-lg max-w-none">
                <div class="bg-linen border-l-4 border-theme-green-dark p-6 mb-8 rounded-r-lg">
                    <h2 class="text-2xl font-semibold text-gray-900 mb-4">SPLOŠNI POGOJI UPORABE IN POSLOVANJA – Kulfood.si</h2>
                    
                    <div class="space-y-2 text-gray-700">
                        <p>
                            <strong class="text-gray-900">KulFood – Rok Vaupotič s.p.</strong>
                        </p>
                        <p>
                            <span class="font-medium">E-pošta:</span> info@kulfood.si
                        </p>
                        <p>
                            <span class="font-medium">Država:</span> Republika Slovenija
                        </p>
                        <p class="text-sm text-gray-600 italic mt-4">
                            <strong class="text-gray-900">Kulfood Rok Vaupotič s.p.</strong>
                            <br>
                            Ajdovščina 4, Ljubljana, 1000 Ljubljana
                            <br>
                            SI56 0284 3026 6815 558
                            <br>
                            Davčna številka: SI 75339358
                            <br>
                            Matična številka: 7397950000
                        </p>
                    </div>
                </div>

                <section class="mb-10">
                    <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">DEFINICIJE (Legenda)</h2>
                    <p class="text-gray-700 mb-6 leading-relaxed">
                        V teh Splošnih pogojih uporabljeni izrazi imajo naslednji pomen:
                    </p>
                    <ul class="space-y-3 mb-6">
                        <li class="flex items-start">
                            <span class="text-theme-green-dark font-bold mr-3 mt-1">•</span>
                            <span class="text-gray-700 flex-1">
                                <strong class="text-gray-900">„Ponudnik":</strong> KulFood – Rok Vaupotič s.p., kot pravni subjekt, nosilec spletne strani in prodaje.
                            </span>
                        </li>
                        <li class="flex items-start">
                            <span class="text-theme-green-dark font-bold mr-3 mt-1">•</span>
                            <span class="text-gray-700 flex-1">
                                <strong class="text-gray-900">„Kupec":</strong> vsaka registrirana fizična ali pravna oseba, ki na spletni strani odda naročilo in zakupi Blago.
                            </span>
                        </li>
                        <li class="flex items-start">
                            <span class="text-theme-green-dark font-bold mr-3 mt-1">•</span>
                            <span class="text-gray-700 flex-1">
                                <strong class="text-gray-900">„Blago":</strong> vsi artikli, ki jih ponuja Ponudnik in jih Kupec lahko naroči preko spletne strani — to vključuje pripravljene obroke, sladice in pijačo.
                            </span>
                        </li>
                        <li class="flex items-start">
                            <span class="text-theme-green-dark font-bold mr-3 mt-1">•</span>
                            <span class="text-gray-700 flex-1">
                                <strong class="text-gray-900">„Spletna stran":</strong> spletno mesto kulfood.si, vključno z vsemi podstranmi, obrazci in funkcionalnostmi.
                            </span>
                        </li>
                        <li class="flex items-start">
                            <span class="text-theme-green-dark font-bold mr-3 mt-1">•</span>
                            <span class="text-gray-700 flex-1">
                                <strong class="text-gray-900">„Naročilo":</strong> enkraten nakup Blaga, ki ga Kupec potrdi preko spletne strani.
                            </span>
                        </li>
                        <li class="flex items-start">
                            <span class="text-theme-green-dark font-bold mr-3 mt-1">•</span>
                            <span class="text-gray-700 flex-1">
                                <strong class="text-gray-900">„Uporabnik":</strong> vsakdo, ki uporablja Spletno stran — vključno s Kupcem in registriranim uporabnikom.
                            </span>
                        </li>
                    </ul>
                    <p class="text-gray-700 leading-relaxed bg-theme-green-light p-4 rounded-lg border-l-4 border-theme-green-dark">
                        Z nadaljnjo rabo Spletne strani, registracijo ali nakupom Blaga Kupec potrjuje, da je definicije razumel in se z njimi strinja.
                    </p>
                </section>

                <section class="mb-10">
                    <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">1. Namen Spletne strani</h2>
                    <p class="text-gray-700 leading-relaxed">
                        Spletna stran služi kot platforma za predstavitev in prodajo Blaga — pripravljene obroke, sladice in pijačo — ter omogoča registracijo uporabnikov, oddajo naročil, spletno plačilo, dostavo in komunikacijo s Ponudnikom.
                    </p>
                </section>

                <section class="mb-10">
                    <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">2. Registracija uporabnikov</h2>
                    <ol class="list-decimal list-inside space-y-3 text-gray-700 ml-4">
                        <li class="pl-2 leading-relaxed">Za oddajo Naročila je potrebna registracija uporabniškega računa.</li>
                        <li class="pl-2 leading-relaxed">Kupec ob registraciji vnese točne, verodostojne in posodobljene podatke.</li>
                        <li class="pl-2 leading-relaxed">Kupec je odgovoren za varovanje prijavnih podatkov (gesla).</li>
                        <li class="pl-2 leading-relaxed">V primeru zlorabe ali suma zlorabe ima Ponudnik pravico začasno omejiti ali izbrisati račun.</li>
                    </ol>
                </section>

                <section class="mb-10">
                    <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">3. Ponudba, naročila in plačila</h2>
                    <ol class="list-decimal list-inside space-y-3 text-gray-700 ml-4">
                        <li class="pl-2 leading-relaxed">Kupec lahko preko spletne strani naroči Blago — obroke, sladice ali pijačo — kot enkratno naročilo. Naročnine niso predvidene.</li>
                        <li class="pl-2 leading-relaxed">Plačilo za Naročilo se izvede preko ponudnika plačilnih storitev Stripe, skladno z njihovimi pogoji uporabe.</li>
                        <li class="pl-2 leading-relaxed">Ponudnik ne odgovarja za delovanje sistemov tretjih oseb (npr. Stripe), tehnične napake, izpade ali druge motnje v obdelavi plačila.</li>
                        <li class="pl-2 leading-relaxed">Vse cene so navedene v evrih (EUR) in vključujejo DDV.</li>
                    </ol>
                </section>

                <section class="mb-10">
                    <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">4. Odpoved naročila</h2>
                    <ol class="list-decimal list-inside space-y-3 text-gray-700 ml-4">
                        <li class="pl-2 leading-relaxed">Kupec lahko Naročilo odpove najkasneje do 01:00 zjutraj na dan dostave.</li>
                        <li class="pl-2 leading-relaxed">Odpoved se mora poslati pisno na naslov info@kulfood.si, z navedbo številke naročila.</li>
                        <li class="pl-2 leading-relaxed">Če je odpoved vložena pravočasno, bo Kupec prejel vračilo vplačanega zneska z odbitkom 2,00 €, kot nadomestilo za administrativne in transakcijske stroške, ki so že nastali ob oddaji in obdelavi naročila.</li>
                        <li class="pl-2 leading-relaxed">Po preteku roka iz zgornje točke odpoved ni več mogoča; Naročilo velja za potrjeno in bo izvedeno.</li>
                        <li class="pl-2 leading-relaxed">Z oddajo Naročila se Kupec strinja z navedenimi pogoji odpovedi in morebitnim odbitkom.</li>
                    </ol>
                </section>

                <section class="mb-10">
                    <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">5. Odstop od pogodbe in reklamacije</h2>
                    
                    <div class="space-y-6">
                        <div>
                            <h3 class="text-xl font-semibold text-gray-900 mb-3">5.1. Izključitev zakonske pravice do odstopa</h3>
                            <p class="text-gray-700 leading-relaxed">
                                Ker Blago vključuje hitro pokvarljivo hrano, sladice in pijačo, Kupec nima pravice do 14-dnevnega odstopa od pogodbe, kot to sicer določa 6. točka prvega odstavka 135. člena Zakona o varstvu potrošnikov (ZVPot).
                            </p>
                        </div>

                        <div>
                            <h3 class="text-xl font-semibold text-gray-900 mb-3">5.2. Pravica do reklamacije zaradi napake</h3>
                            <p class="text-gray-700 mb-3 leading-relaxed">
                                Kupec lahko uveljavlja reklamacijo v primeru, da Blago ob dostavi:
                            </p>
                            <ul class="list-disc list-inside space-y-2 text-gray-700 ml-6">
                                <li class="leading-relaxed">ni skladno z dogovorjeno ponudbo,</li>
                                <li class="leading-relaxed">je očitno poškodovano,</li>
                                <li class="leading-relaxed">ne ustreza standardom varnosti živil ali kakovosti,</li>
                                <li class="leading-relaxed">obstaja druga stvarna napaka, ki izhaja iz proizvodnje, pakiranja ali dostave.</li>
                            </ul>
                        </div>

                        <div>
                            <h3 class="text-xl font-semibold text-gray-900 mb-3">5.3. Rok za vložitev reklamacije</h3>
                            <p class="text-gray-700 leading-relaxed">
                                Zaradi narave Blaga (hitro pokvarljivo živilo) mora Kupec morebitno napako sporočiti najkasneje v roku ene (1) ure po dostavi. Po izteku tega roka reklamacija v splošnem ni več možna, razen v primeru, da Kupec dokaže, da napake ni mogel takoj odkriti.
                            </p>
                        </div>

                        <div>
                            <h3 class="text-xl font-semibold text-gray-900 mb-3">5.4. Postopek reklamacije</h3>
                            <p class="text-gray-700 mb-3 leading-relaxed">
                                Reklamacija mora biti poslana na info@kulfood.si in mora vsebovati:
                            </p>
                            <ul class="list-disc list-inside space-y-2 text-gray-700 ml-6">
                                <li class="leading-relaxed">številko naročila,</li>
                                <li class="leading-relaxed">opis napake,</li>
                                <li class="leading-relaxed">fotografije dostavljenega Blaga (izdelek in embalaža),</li>
                                <li class="leading-relaxed">čas prejema Blaga.</li>
                            </ul>
                            <p class="text-gray-700 mt-3 leading-relaxed">
                                Ponudnik lahko po potrebi zahteva dodatna dokazila in morebiti ogled Blaga, če je le to še možno.
                            </p>
                        </div>

                        <div>
                            <h3 class="text-xl font-semibold text-gray-900 mb-3">5.5. Odgovor in reševanje reklamacije</h3>
                            <ul class="list-disc list-inside space-y-2 text-gray-700 ml-6">
                                <li class="leading-relaxed">Ponudnik bo reklamacijo obdelal in o odločitvi obvestil Kupca v največ 8 dneh od prejema zahtevka.</li>
                                <li class="leading-relaxed">Če je reklamacija utemeljena, ima Kupec pravico do vračila kupnine, zamenjave Blaga ali drugega ustreznega nadomestila.</li>
                                <li class="leading-relaxed">Če reklamacija ni utemeljena, bo Kupec o tem pisno obveščen z obrazložitvijo.</li>
                            </ul>
                        </div>

                        <div>
                            <h3 class="text-xl font-semibold text-gray-900 mb-3">5.6. Reklamacija ni mogoča, če:</h3>
                            <ul class="list-disc list-inside space-y-2 text-gray-700 ml-6">
                                <li class="leading-relaxed">je Blago nepravilno skladiščen ali neupravičeno rokovano po prevzemu (napačna temperatura, higiena),</li>
                                <li class="leading-relaxed">je Blago že v delni ali popolni meri zaužito,</li>
                                <li class="leading-relaxed">napaka ali nezadovoljstvo izhaja iz subjektivne ocene (npr. okus),</li>
                                <li class="leading-relaxed">rok ene ure za prijavo reklamacije je potekel brez utemeljenega razloga.</li>
                            </ul>
                        </div>
                    </div>
                </section>

                <section class="mb-10">
                    <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">6. Varstvo osebnih podatkov in zasebnost</h2>
                    <p class="text-gray-700 leading-relaxed italic">
                        (Obstoječa Politika zasebnosti ostaja v veljavi, pri čemer se opredelitev Blaga ne spremeni — gre za nabor artiklov, za katere se izvajajo storitve.)
                    </p>
                </section>

                <section class="mb-10">
                    <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">7. Avtorske pravice in prepoved zlorabe</h2>
                    <p class="text-gray-700 leading-relaxed">
                        Vsebina Spletne strani je zaščitena; reproduciranje, distribucija, kopiranje ali kakršnakoli zloraba brez izrecnega soglasja Ponudnika je prepovedana. Kršitev pomeni civilno in/ali kazensko odgovornost.
                    </p>
                </section>

                <section class="mb-10">
                    <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">8. Omejitev odgovornosti</h2>
                    <p class="text-gray-700 mb-3 leading-relaxed">
                        Ponudnik ne odgovarja za:
                    </p>
                    <ul class="list-disc list-inside space-y-2 text-gray-700 ml-6">
                        <li class="leading-relaxed">tehnične napake ali izpade sistema,</li>
                        <li class="leading-relaxed">delovanje sistemov tretjih oseb (npr. plačilnih),</li>
                        <li class="leading-relaxed">škodo, nastalo zaradi neupoštevanja navodil za rokovanje z Blagom po prevzemu,</li>
                        <li class="leading-relaxed">morebitne posledice subjektivne ocene kakovosti ali okusa Blaga.</li>
                    </ul>
                </section>

                <section class="mb-10">
                    <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">9. Pravo in pristojnost</h2>
                    <p class="text-gray-700 leading-relaxed">
                        Ti pogoji se razlagajo v skladu s pravom Republike Slovenije. Morebitni spori se rešujejo pred pristojnim sodiščem v Ljubljani.
                    </p>
                </section>

                <section class="mb-10">
                    <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">10. Spremembe pogojev</h2>
                    <p class="text-gray-700 leading-relaxed">
                        Ponudnik si pridržuje pravico do spremembe teh Splošnih pogojev. Spremembe začnejo veljati z dnem objave na Spletni strani. Kupec je dolžan redno spremljati pogoje in se z njimi seznaniti.
                    </p>
                </section>

                <div class="mt-12 pt-6 border-t-2 border-gray-200">
                    <p class="text-sm text-gray-500 text-center">
                        Zadnja posodobitev: 30. 12. 2025
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>
